import { createTheme } from '@material-ui/core';
import { ptBR } from '@material-ui/core/locale';

import { appColors } from '../AppTheme';

/**
 * Constroi o tema MUI a partir da configuracao do parceiro ativo.
 *
 * Este DEVE ser o unico `createTheme` em toda a aplicacao.
 * Todos os componentes consomem tokens via `useTheme()`, `sx` prop ou `styled` do MUI.
 *
 * Alem do tema MUI padrao, injeta propriedades customizadas:
 * - `theme.screens` — tokens de cor segmentados por tela/contexto (login, menu, header, input, status, checkbox, divider)
 * - `theme.partnerComponents` — overrides de componentes (title, subtitle, text)
 *
 * Acessiveis em styled-components via `${({ theme }) => theme.screens.menu.background}`.
 *
 * @param {import('../config/partners/index').PartnerConfig} partnerConfig - Configuracao completa do parceiro
 * @returns {import('@mui/material/styles').Theme} Objeto MUI theme com propriedades customizadas
 */
export function buildMuiTheme(partnerConfig) {
  const { colors, typography, components: partnerComponents = {} } = partnerConfig;
  const calendar = partnerComponents.customDatePicker?.calendar || {};

  const theme = createTheme(
    {
      palette: {
        primary: {
          main: colors.primary,
          dark: colors.primaryDark,
          light: colors.primaryLight,
        },
        secondary: {
          main: colors.secondary,
        },
        success: {
          main: colors.success,
        },
        error: {
          main: colors.error,
        },
        warning: {
          main: colors.warning,
        },
        info: {
          main: colors.info,
        },
        background: {
          default: colors.background,
          paper: colors.surface,
        },
        text: {
          primary: colors.textPrimary,
          secondary: colors.textSecondary,
          disabled: colors.textDisabled,
        },
        divider: colors.divider,
        common: {
          white: '#ffffff',
          black: '#000000',
        },
        // Tokens customizados acessiveis via theme.palette
        border: {
          main: colors.border,
          primary: colors.borderPrimary,
        },
        focus: {
          main: colors.focus,
        },
        inverted: {
          main: colors.inverted,
        },
        hover: {
          main: colors.hover || null,
        },
      },
      appColors,
      typography: {
        fontFamily: typography.fontFamily,
        fontFamilyTitle: typography.fontFamilyTitle,
        descriptionFontFamily: typography.descriptionFontFamily,
        inputFieldFontFamily: typography.inputFieldFontFamily,
        inputLabelFontFamily:
          typography.inputLabelFontFamily || typography.fontFamily,
        textFontFamilyMap: typography.textFontFamilyMap,
        weights: typography.weights,
        fontWeightRegular: typography.weights.regular,
        fontWeightMedium: typography.weights.medium,
        fontWeightBold: typography.weights.bold,
        // Peso customizado para semiBold
        fontWeightSemiBold: typography.weights.semiBold,
        body1: {
          fontFamily: typography.fontFamily,
        },
        subtitle1: {
          fontFamily: typography.fontFamily,
        },
        bancoDoBrasilTitulos: {
          fontFamily: 'BancoDoBrasilTitulos',
        },
        bancoDoBrasilTextos: {
          fontFamily: 'BancoDoBrasilTextos',
        },
        inter: {
          fontFamily: 'Inter',
        },
      },
      overrides: {
        MuiPickersToolbar: {
          toolbar: {
            backgroundColor: calendar.headerBackground,
          },
        },
        MuiPickersToolbarText: {
          toolbarTxt: {
            color: calendar.headerText,
          },
          toolbarBtnSelected: {
            color: calendar.headerText,
          },
        },
        MuiPickersDay: {
          daySelected: {
            backgroundColor: calendar.daySelected,
            color: calendar.daySelectedText,
            '&:hover': {
              backgroundColor: calendar.daySelected,
            },
          },
        },
      },
    },
    ptBR,
  );

  // Injetar propriedades customizadas no objeto theme MUI.
  // Acessiveis em styled-components via ${({ theme }) => theme.screens.*}
  theme.screens = partnerConfig.screens || {};
  theme.partnerComponents = partnerConfig.components || {};

  return theme;
}
